<script setup lang="ts">
import { computed } from 'vue'
import { usePreferredReducedMotion } from '@vueuse/core'
import type { ChatMessage } from '../types/chatbot'
import { useTypewriter } from '../composables/useTypewriter'

const props = defineProps<{
  message: ChatMessage
}>()

const isUser = computed(() => props.message.role === 'user')

// Captured once: a message that is born in the streaming state is the live reply
// and gets the typewriter. Greeting, history and user messages render at once.
const isLive = props.message.streaming === true
const reducedMotion = usePreferredReducedMotion()

const source = computed(() => props.message.content)
const streaming = computed(() => props.message.streaming === true)
const { displayed, done } = useTypewriter(source, streaming, {
  enabled: isLive && reducedMotion.value !== 'reduce',
})

const renderText = computed(() => (isLive ? displayed.value : props.message.content))
const showCaret = computed(() => isLive && !done.value)

const formattedContent = computed(() => {
  let text = renderText.value
  text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  text = text.replace(/\*(.+?)\*/g, '<em>$1</em>')
  text = text.replace(/`(.+?)`/g, '<code>$1</code>')
  text = text.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
  text = text.replace(/\n/g, '<br>')
  return text
})

const hasSourceDocs = computed(
  () => !!props.message.sourceDocs && props.message.sourceDocs.length > 0,
)
</script>

<template>
  <div class="chat-message" :class="{ 'chat-message--user': isUser, 'chat-message--bot': !isUser }">
    <div v-if="!isUser" class="chat-message__avatar">AI</div>

    <div class="chat-message__bubble">
      <div class="chat-message__content">
        <span v-html="formattedContent" /><span v-if="showCaret" class="chat-message__caret" />
      </div>

      <details v-if="hasSourceDocs" class="chat-message__sources">
        <summary class="sources-summary">Источники ({{ message.sourceDocs?.length }})</summary>
        <div v-for="doc in message.sourceDocs" :key="doc.id" class="source-item">
          <span class="source-type">{{ doc.source_type }}</span>
          <a
            v-if="doc.url"
            class="source-title source-title--link"
            :href="doc.url"
            target="_blank"
            rel="noopener"
          >{{ doc.title }}</a>
          <span v-else class="source-title">{{ doc.title }}</span>
        </div>
      </details>
    </div>
  </div>
</template>

<style scoped>
.chat-message {
  display: flex;
  gap: 8px;
  padding: 2px 0;
  align-items: flex-end;
}

.chat-message--user {
  flex-direction: row-reverse;
}

.chat-message__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #2563eb;
  color: white;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.chat-message__bubble {
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 16px;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.chat-message--bot .chat-message__bubble {
  background: #f3f4f6;
  color: #1f2937;
  border-bottom-left-radius: 4px;
}

.chat-message--user .chat-message__bubble {
  background: #2563eb;
  color: white;
  border-bottom-right-radius: 4px;
}

.chat-message__content :deep(a) {
  color: inherit;
  text-decoration: underline;
}

.chat-message--bot .chat-message__content :deep(a) {
  color: #2563eb;
}

.chat-message__content :deep(code) {
  background: rgba(0, 0, 0, 0.08);
  padding: 1px 4px;
  border-radius: 3px;
  font-size: 13px;
}

.chat-message__content :deep(strong) {
  font-weight: 600;
}

/* Typing caret — a thin bar that blinks while the reply streams in. */
.chat-message__caret {
  display: inline-block;
  width: 2px;
  height: 1em;
  margin-left: 1px;
  vertical-align: text-bottom;
  border-radius: 1px;
  background: #2563eb;
  animation: caret-blink 1s steps(2, start) infinite;
}

@keyframes caret-blink {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0;
  }
}

.chat-message__sources {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
  font-size: 12px;
}

.sources-summary {
  color: #6b7280;
  cursor: pointer;
  user-select: none;
  list-style: none;
}

.sources-summary::-webkit-details-marker {
  display: none;
}

.sources-summary::before {
  content: '▸';
  display: inline-block;
  margin-right: 4px;
  transition: transform 0.15s ease;
}

.chat-message__sources[open] .sources-summary::before {
  transform: rotate(90deg);
}

.chat-message__sources[open] .sources-summary {
  margin-bottom: 4px;
}

.source-item {
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 2px;
}

.source-type {
  background: #e5e7eb;
  color: #6b7280;
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  flex-shrink: 0;
}

.source-title {
  color: #2563eb;
  font-size: 12px;
}

.source-title--link {
  text-decoration: underline;
  cursor: pointer;
}

.source-title--link:hover {
  color: #1d4ed8;
}

@media (prefers-reduced-motion: reduce) {
  .chat-message__caret {
    animation: none;
  }
}
</style>
